import { ThemeEvents, VariantUpdateEvent } from "@theme/events";

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 * Additionally, it calculates and displays total price including selected addons.
 */
class ProductPrice extends HTMLElement {
  /**
   * Checks if this price component belongs to an addon
   * @returns {boolean} True if this is an addon price
   */
  isAddonPrice() {
    // Check if this price element is inside an addon card
    return this.closest("product-addon-card") !== null;
  }

  /**
   * Checks if this is the total price component (product-summary-total-price block)
   * @returns {boolean} True if this is the total price component
   */
  isTotalPriceComponent() {
    // Check if this component has the total price block ID
    return !!(
      this.dataset.blockId &&
      this.dataset.blockId.includes("product-summary-total-price")
    );
  }

  connectedCallback() {
    const closestSection = this.closest(".shopify-section, dialog");
    if (!closestSection) return;
    closestSection.addEventListener(
      ThemeEvents.variantUpdate,
      this.updatePrice
    );

    // Listen for global variant updates to handle multiple price instances
    document.addEventListener(
      ThemeEvents.variantUpdate,
      this.handleGlobalVariantUpdate
    );

    // Only the total price component should handle addon calculations
    if (this.isTotalPriceComponent()) {
      document.addEventListener("addon:change", this.updateTotalPrice);
      // Initialize total price calculation
      this.initializeTotalPrice();
    }
  }

  disconnectedCallback() {
    const closestSection = this.closest(".shopify-section, dialog");
    if (!closestSection) return;
    closestSection.removeEventListener(
      ThemeEvents.variantUpdate,
      this.updatePrice
    );

    // Remove global variant update listener
    document.removeEventListener(
      ThemeEvents.variantUpdate,
      this.handleGlobalVariantUpdate
    );

    // Only remove addon change listener from total price component
    if (this.isTotalPriceComponent()) {
      document.removeEventListener("addon:change", this.updateTotalPrice);
    }
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else {
      // Filter by productId to ensure only the correct product's price is updated
      const eventProductId = event.detail.data.productId;
      const currentProductId = this.dataset.productId;

      if (
        eventProductId &&
        currentProductId &&
        eventProductId !== currentProductId
      ) {
        return;
      }
    }

    // Find the specific product-price element for this product
    let newPrice;
    if (event.detail.data.newProduct) {
      // For new products, find by the new product ID
      const newProductPriceElement = event.detail.data.html.querySelector(
        `product-price[data-product-id="${event.detail.data.newProduct.id}"]`
      );
      newPrice = newProductPriceElement?.querySelector(
        '[ref="priceContainer"]'
      );
    } else {
      // For same product variant changes, find by current product ID
      const currentProductPriceElement = event.detail.data.html.querySelector(
        `product-price[data-product-id="${this.dataset.productId}"]`
      );
      newPrice = currentProductPriceElement?.querySelector(
        '[ref="priceContainer"]'
      );
    }
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice.cloneNode(true));
      // Recalculate total price after any price update
      if (this.isTotalPriceComponent()) {
        this.updateTotalPrice();
      } else if (this.isAddonPrice()) {
        // If this is an addon price that changed, notify the total price component
        this.notifyTotalPriceUpdate();
      }
    }
  };

  /**
   * Handles global variant updates to update multiple price instances of the same product
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  handleGlobalVariantUpdate = (event) => {
    // Filter by productId to ensure only the correct product's price is updated
    const eventProductId = event.detail.data.productId;
    const currentProductId = this.dataset.productId;

    if (
      eventProductId &&
      currentProductId &&
      eventProductId !== currentProductId
    ) {
      return;
    }

    // Skip if this event was already handled by the local updatePrice method
    if (
      event.target instanceof HTMLElement &&
      this.closest(".shopify-section, dialog") ===
        event.target.closest(".shopify-section, dialog")
    ) {
      return;
    }

    // Find the updated price from the HTML response
    let newPrice;
    if (event.detail.data.newProduct) {
      // For new products, find by the new product ID
      const newProductPriceElement = event.detail.data.html.querySelector(
        `product-price[data-product-id="${event.detail.data.newProduct.id}"]`
      );
      newPrice = newProductPriceElement?.querySelector(
        '[ref="priceContainer"]'
      );
    } else {
      // For same product variant changes, find by current product ID
      const currentProductPriceElement = event.detail.data.html.querySelector(
        `product-price[data-product-id="${this.dataset.productId}"]`
      );
      newPrice = currentProductPriceElement?.querySelector(
        '[ref="priceContainer"]'
      );
    }

    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice.cloneNode(true));
      // Recalculate total price after any price update
      if (this.isTotalPriceComponent()) {
        this.updateTotalPrice();
      } else if (this.isAddonPrice()) {
        // If this is an addon price that changed, notify the total price component
        this.notifyTotalPriceUpdate();
      }
    }
  };

  /**
   * Initializes the total price calculation on component load
   */
  initializeTotalPrice = () => {
    // Add a small delay to ensure DOM is fully loaded
    setTimeout(() => {
      this.updateTotalPrice();
    }, 100);
  };

  /**
   * Updates the total price including addons
   * Only applies to the total price component
   */
  updateTotalPrice = () => {
    // Only calculate total for the dedicated total price component
    if (!this.isTotalPriceComponent()) return;

    const basePrice = this.getBaseProductPrice();
    const addonsPrice = this.getSelectedAddonsPrice();
    const totalPrice = basePrice + addonsPrice;

    this.displayTotalPrice(totalPrice, addonsPrice);
  };

  /**
   * Gets the base product price from the main product (not addons)
   * @returns {number} Base product price in cents
   */
  getBaseProductPrice() {
    // Find the main product price (not inside addon cards)
    const mainProductPrice = document.querySelector(
      "product-price:not(product-addon-card product-price) .price"
    );
    if (!mainProductPrice || !mainProductPrice.textContent) return 0;

    // Get original price if available, otherwise current price
    const priceText =
      mainProductPrice instanceof HTMLElement &&
      mainProductPrice.dataset.originalPrice
        ? mainProductPrice.dataset.originalPrice
        : mainProductPrice.textContent.trim();

    const priceMatch = priceText.match(/[\d,\.]+/);
    if (!priceMatch) return 0;

    // Convert to cents (multiply by 100 and remove decimal points)
    const priceValue = parseFloat(priceMatch[0].replace(/,/g, ""));
    return Math.round(priceValue * 100);
  }

  /**
   * Gets the total price of selected addons
   * @returns {number} Total addons price in cents
   */
  getSelectedAddonsPrice() {
    let totalAddonsPrice = 0;

    // Find all selected addon cards
    const selectedAddonInputs = document.querySelectorAll(
      'input[name^="addon-"]:checked'
    );

    selectedAddonInputs.forEach((input) => {
      if (input instanceof HTMLInputElement && input.value === "true") {
        const addonCard = input.closest("product-addon-card");
        if (addonCard) {
          const addonPriceElement = addonCard.querySelector(
            ".addon-price .price"
          );
          if (addonPriceElement && addonPriceElement.textContent) {
            const addonPriceText = addonPriceElement.textContent.trim();
            const addonPriceMatch = addonPriceText.match(/[\d,\.]+/);
            if (addonPriceMatch) {
              const addonPriceValue = parseFloat(
                addonPriceMatch[0].replace(/,/g, "")
              );
              totalAddonsPrice += Math.round(addonPriceValue * 100);
            }
          }
        }
      }
    });

    return totalAddonsPrice;
  }

  /**
   * Displays the total price including addons
   * @param {number} totalPrice - Total price in cents
   * @param {number} addonsPrice - Addons price in cents
   */
  displayTotalPrice(totalPrice, addonsPrice) {
    // Find the dedicated total price element (product-summary-total-price block)
    const totalPriceElement = document.querySelector(
      'product-price[data-block-id*="product-summary-total-price"] .price'
    );

    if (!totalPriceElement) {
      // Fallback: if no dedicated total price element exists, don't modify any price
      console.warn(
        "No dedicated total price element found. Total price will not be displayed."
      );
      return;
    }

    // Get currency symbol from existing price display
    const currencySymbol = this.extractCurrencySymbol(
      totalPriceElement.textContent || ""
    );

    // Store original price if not already stored
    if (
      totalPriceElement instanceof HTMLElement &&
      !totalPriceElement.dataset.originalPrice
    ) {
      totalPriceElement.dataset.originalPrice =
        totalPriceElement.textContent || "";
    }

    if (addonsPrice > 0) {
      // Update total price element to show calculated total
      const formattedTotalPrice = this.formatPrice(totalPrice, currencySymbol);
      totalPriceElement.textContent = formattedTotalPrice;
    } else {
      // Restore original price
      if (
        totalPriceElement instanceof HTMLElement &&
        totalPriceElement.dataset.originalPrice
      ) {
        totalPriceElement.textContent = totalPriceElement.dataset.originalPrice;
      }
    }
  }

  /**
   * Extracts currency symbol from price text
   * @param {string} priceText - Price text containing currency symbol
   * @returns {string} Currency symbol
   */
  extractCurrencySymbol(priceText) {
    const currencyMatch = priceText.match(/[^\d\s,\.]+/);
    return currencyMatch ? currencyMatch[0] : "$";
  }

  /**
   * Formats price from cents to display format using Shopify money format
   * @param {number} priceInCents - Price in cents
   * @param {string} currencySymbol - Currency symbol (fallback)
   * @returns {string} Formatted price string
   */
  formatPrice(priceInCents, currencySymbol) {
    // Try to get Shopify money format from existing price elements
    const moneyFormat = this.getMoneyFormat();

    if (moneyFormat) {
      return this.formatMoneyWithTemplate(priceInCents, moneyFormat);
    }

    // Fallback to simple format
    const priceValue = priceInCents / 100;
    return `${currencySymbol}${priceValue.toFixed(2)}`;
  }

  /**
   * Gets the Shopify money format template
   * @returns {string|null} Money format template or null
   */
  getMoneyFormat() {
    // Try to find money format from existing price elements
    const priceElement = document.querySelector("[data-money-format]");
    if (priceElement instanceof HTMLElement) {
      return priceElement.dataset.moneyFormat || null;
    }

    // Try to extract format from existing formatted prices
    const existingPrice = document.querySelector(".price");
    if (existingPrice && existingPrice.textContent) {
      // Extract format pattern from existing price
      const priceText = existingPrice.textContent.trim();
      if (priceText.match(/^\$[\d,]+\.\d{2}$/)) {
        return "${{amount}}";
      }
      if (priceText.match(/^[\d,]+\.\d{2}\s*\$$/)) {
        return "{{amount}} $";
      }
      if (priceText.match(/^\$\s*[\d,]+\.\d{2}$/)) {
        return "$ {{amount}}";
      }
    }

    return null;
  }

  /**
   * Formats money using Shopify money format template
   * @param {number} priceInCents - Price in cents
   * @param {string} template - Money format template
   * @returns {string} Formatted price string
   */
  formatMoneyWithTemplate(priceInCents, template) {
    const priceValue = priceInCents / 100;

    return template.replace(/{\{\s*(\w+)\s*}\}/g, (match, placeholder) => {
      switch (placeholder) {
        case "amount":
          return this.formatCents(priceInCents, ",", ".", 2);
        case "amount_no_decimals":
          return this.formatCents(priceInCents, ",", ".", 0);
        case "amount_with_comma_separator":
          return this.formatCents(priceInCents, ".", ",", 2);
        case "amount_no_decimals_with_comma_separator":
          return this.formatCents(priceInCents, ".", ",", 0);
        case "amount_no_decimals_with_space_separator":
          return this.formatCents(priceInCents, " ", ".", 0);
        case "amount_with_space_separator":
          return this.formatCents(priceInCents, " ", ",", 2);
        case "amount_with_period_and_space_separator":
          return this.formatCents(priceInCents, " ", ".", 2);
        case "amount_with_apostrophe_separator":
          return this.formatCents(priceInCents, "'", ".", 2);
        default:
          return match;
      }
    });
  }

  /**
   * Formats money in cents with separators
   * @param {number} moneyValue - The money value in cents
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split(".");
    if (!a) a = "0";
    if (!b) b = "";

    // Split by groups of 3 digits
    a = a.replace(
      /\d(?=(\d\d\d)+(?!\d))/g,
      (digit) => digit + thousandsSeparator
    );

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, "0");
  }

  /**
   * Notifies the total price component to recalculate when addon prices change
   */
  notifyTotalPriceUpdate() {
    // Find the total price component and trigger recalculation
    const totalPriceComponent = document.querySelector(
      'product-price[data-block-id*="product-summary-total-price"]'
    );
    if (totalPriceComponent && totalPriceComponent instanceof ProductPrice) {
      totalPriceComponent.updateTotalPrice();
    }
  }
}

if (!customElements.get("product-price")) {
  customElements.define("product-price", ProductPrice);
}
